import React, { KeyboardEvent, useEffect, useRef } from 'react';

import {
  ActiveBPWrapper,
  BPItem,
  BPListWrapper,
  Container,
  StyledLoaderWrapper,
} from './styles';
import { BusinessPartnerSelectorItem } from './types';
import Loader from '../../../components/Loader/Loader';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import ActiveBusinessPartnerDisplay from './SubComponents/ActiveBusinessPartnerDisplay';
import AccessOnBehalfControl from './SubComponents/AccessOnBehalfControl';
import BusinessPartnerSelectionList from './SubComponents/BusinessPartnerSelectionList';

interface BusinessPartnerSelectorProps {
  hasAob: boolean;
  isAccessOnBehalf?: boolean;
  aobActivationError: TranslationKeys | undefined;
  aobInputValue: string | undefined;
  activeBPNumber?: string;
  bpNumbers: BusinessPartnerSelectorItem[];
  recentlyUsedBPs?: string[];
  aobInputValueChanged: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBPSelect: (bp: BusinessPartnerSelectorItem) => void;
  onAobStart: (bp: string) => void;
  onAobEnd: () => void;
  isListOpen: boolean;
  isLoading: boolean;
  setIsListOpen: (isOpen: boolean) => void;
}

const BusinessPartnerSelector: React.FC<BusinessPartnerSelectorProps> = ({
  hasAob,
  isAccessOnBehalf,
  aobActivationError,
  aobInputValue,
  aobInputValueChanged,
  activeBPNumber,
  bpNumbers,
  onBPSelect,
  onAobStart,
  onAobEnd,
  isListOpen,
  isLoading,
  setIsListOpen,
}) => {
  const bpListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        bpListRef.current &&
        !bpListRef.current.contains(event.target as Node)
      ) {
        setIsListOpen(false);
      }
    };

    if (isListOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isListOpen, setIsListOpen]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      const activeBP = getActiveBusinessPartnerObject();
      if (activeBP) {
        onBPSelect(activeBP);
      }
    }
  };

  const getActiveBusinessPartnerObject = () => {
    return bpNumbers.filter(
      (bp) => bp.businessPartnerNumber == activeBPNumber
    )[0];
  };

  const renderLoadingContent = () => {
    return (
      <ActiveBPWrapper
        style={{
          cursor: bpNumbers.length > 1 ? 'pointer' : 'default',
        }}
      >
        <BPItem>
          <StyledLoaderWrapper>
            <Loader />
          </StyledLoaderWrapper>
        </BPItem>
      </ActiveBPWrapper>
    );
  };

  const renderInactiveState = () => {
    const activeBusinessPartnerObject = getActiveBusinessPartnerObject();

    return (
      <ActiveBusinessPartnerDisplay
        selectedBusinessPartner={activeBusinessPartnerObject}
        onClick={() => setIsListOpen(!isListOpen)}
        isListOpen={isListOpen}
        onKeyDown={handleKeyDown}
        aobActive={isAccessOnBehalf}
        onAobEnd={onAobEnd}
      />
    );
  };

  const renderActiveState = () => {
    const activeBusinessPartnerObject = getActiveBusinessPartnerObject();

    return (
      <BPListWrapper isListOpen={isListOpen}>
        <ActiveBusinessPartnerDisplay
          selectedBusinessPartner={activeBusinessPartnerObject}
          isListOpen={isListOpen}
          onClick={() => setIsListOpen(false)}
          onKeyDown={handleKeyDown}
          aobActive={isAccessOnBehalf}
          onAobEnd={onAobEnd}
        />
        {hasAob && (
          <AccessOnBehalfControl
            aobActivationError={aobActivationError}
            aobInputValue={aobInputValue}
            aobInputValueChanged={aobInputValueChanged}
            onAobEnd={onAobEnd}
            onAobStart={onAobStart}
            onBPSelect={onBPSelect}
            aobActive={isAccessOnBehalf}
          />
        )}
        <BusinessPartnerSelectionList
          bpItems={bpNumbers.filter(
            (b) => b.businessPartnerNumber !== activeBPNumber
          )}
          onBPSelect={onBPSelect}
          isListOpen={isListOpen}
        />
      </BPListWrapper>
    );
  };

  return (
    <Container ref={bpListRef}>
      {isLoading
        ? renderLoadingContent()
        : isListOpen
          ? renderActiveState()
          : renderInactiveState()}
    </Container>
  );
};

export default BusinessPartnerSelector;
